import { getNotificationStreamEndpoint, getNotificationStreamHeaders } from '../api/notificationStreamApi'

class UnifiedJobStreamService {
  constructor() {
    this.controller = null
    this.isConnected = false
    this.isConnecting = false
    this.connectedLanguage = null
    this.reconnectTimer = null
    this.retryCount = 0
    this.listeners = new Set()
  }

  addListener(callback) {
    this.listeners.add(callback)
    return () => {
      this.listeners.delete(callback)
    }
  }

  notifyListeners(data) {
    this.listeners.forEach((listener) => {
      try {
        listener(data)
      } catch (err) {
        console.error('[UnifiedJobStream] Listener callback error:', err)
      }
    })
  }

  getHeaders(custom = {}) {
    return getNotificationStreamHeaders(custom)
  }

  connect(forceReconnect = false, languageOverride = null) {
    const hasAuth = typeof window !== 'undefined' && Boolean(localStorage.getItem('access') || localStorage.getItem('user'))
    if (!hasAuth) {
      console.warn('[UnifiedJobStream] Not connecting: User is not authenticated.')
      return
    }

    const headers = this.getHeaders(languageOverride ? { 'x-language': languageOverride } : {})
    if (!headers['x-user-id'] && !localStorage.getItem('access')) {
      console.warn('[UnifiedJobStream] Delaying connection: User ID not available yet.')
      return
    }

    const currentLang = languageOverride || headers['x-language'] || 'en'

    if (this.isConnected || this.isConnecting) {
      if (this.connectedLanguage && this.connectedLanguage !== currentLang) {
        console.log(`[UnifiedJobStream] Language changed from ${this.connectedLanguage} to ${currentLang}. Reconnecting stream...`)
        this.disconnect()
      } else if (!forceReconnect) {
        return
      }
    }

    this.isConnecting = true
    this.controller = new AbortController()
    const signal = this.controller.signal

    const streamUrl = getNotificationStreamEndpoint()

    console.log('[UnifiedJobStream] Establishing connection to:', streamUrl, 'Language:', currentLang)

    const fetchStream = async () => {
      try {
        const response = await fetch(streamUrl, {
          method: 'GET',
          headers,
          signal
        })

        if (!response.ok) {
          throw new Error(`HTTP Error ${response.status}: ${response.statusText}`)
        }

        this.isConnected = true
        this.isConnecting = false
        this.connectedLanguage = currentLang
        this.retryCount = 0

        const reader = response.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let dataAccumulator = ''
        let currentEventType = 'message'
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (value) {
            buffer += decoder.decode(value, { stream: true })
          }

          const lines = buffer.split(/\r?\n/)

          if (done) {
            buffer = ''
          } else {
            buffer = lines.pop() || ''
          }

          for (const line of lines) {
            if (signal.aborted) break
            const trimmed = line.trim()

            if (!trimmed) {
              if (dataAccumulator) {
                try {
                  const data = JSON.parse(dataAccumulator)
                  this.handleMessage(data, currentEventType)
                } catch (e) {
                  console.warn('[UnifiedJobStream] JSON Parse error:', e, 'Data:', dataAccumulator)
                }
                dataAccumulator = ''
                currentEventType = 'message'
              }
              continue
            }

            if (trimmed.startsWith(':')) continue // Comment line

            if (trimmed.startsWith('event:')) {
              currentEventType = trimmed.replace(/^event:\s*/, '').trim()
            } else if (trimmed.startsWith('data:')) {
              const dataContent = trimmed.replace(/^data:\s*/, '').trim()
              if (dataContent === '[DONE]') {
                dataAccumulator = ''
                continue
              }
              dataAccumulator += (dataAccumulator ? '\n' : '') + dataContent
            } else if (dataAccumulator) {
              dataAccumulator += (dataAccumulator ? '\n' : '') + trimmed
            }
          }

          if (done || signal.aborted) break
        }

        if (!signal.aborted) {
          this.scheduleReconnect()
        }
      } catch (err) {
        if (err.name !== 'AbortError' && !signal.aborted) {
          console.warn('[UnifiedJobStream] Stream connection error:', err.message)
          this.scheduleReconnect()
        }
      } finally {
        this.isConnected = false
        this.isConnecting = false
      }
    }

    fetchStream()
  }

  handleMessage(data, eventType) {
    if (!data || typeof data !== 'object') return
    const payload = {
      ...data,
      event_type: eventType || data.event_type || data.status
    }
    this.notifyListeners(payload)
  }

  scheduleReconnect() {
    this.isConnected = false
    this.isConnecting = false
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)

    const hasAuth = typeof window !== 'undefined' && Boolean(localStorage.getItem('access') || localStorage.getItem('user'))
    if (!hasAuth) {
      console.log('[UnifiedJobStream] Skipping reconnect: User is not authenticated.')
      return
    }

    this.retryCount += 1
    const delay = Math.min(30000, Math.pow(2, Math.min(this.retryCount, 5)) * 1000)
    console.log(`[UnifiedJobStream] Reconnecting in ${delay / 1000}s (attempt ${this.retryCount})...`)

    this.reconnectTimer = setTimeout(() => {
      this.connect()
    }, delay)
  }

  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.controller) {
      this.controller.abort()
      this.controller = null
    }
    this.isConnected = false
    this.isConnecting = false
    this.retryCount = 0
    console.log('[UnifiedJobStream] Disconnected.')
  }
}

export const unifiedJobStreamService = new UnifiedJobStreamService()
